<template>
  <Popup :ref="(el) => popup = el"
         class=""
         :title="$t('limits.title')"
         @onClose="hide"
  >
    <LoaderContent ref="loader" class="">
      <div class="" style="overflow: auto">
        <div v-if="plans && plans.length > 0">
          <div v-for="plan in plans" :key="plan.settingId" class="plan-item mb-4">
            <div class="plan-header">
              <div class="plan-currency">
                <div class="plan-type-row">
                  <span class="plan-type-label">{{ helper.trans('limits.plan_type_label') }}:</span>
                  <span class="plan-type-badge">{{ helper.trans('limits.plan_type.' + (plan.planType || 'mandatory')) }}</span>
                </div>
                <div class="plan-type-row">
                  <span class="plan-type-label">{{ helper.trans('limits.reward_type_label') }}:</span>
                  <span class="plan-type-badge">{{ helper.trans('limits.reward_type.' + (plan.rewardType || 'unlock_requisites')) }}</span>
                  <span v-if="plan.rewardType === 'priority_boost' && plan.rewardPriority" class="plan-type-badge plan-type-badge--priority">{{ helper.trans('limits.reward_priority.' + plan.rewardPriority) }}</span>
                </div>
                <span class="currency-code">{{ plan.currency }}</span>
                <span v-if="plan.limit" class="plan-limit">
                  {{ helper.trans('limits.limit') }}: {{ plan.limit }}
                </span>
              </div>
              <div class="plan-status">
                <span 
                  class="status-badge" 
                  :class="{
                    'status-active': plan.isActive,
                    'status-inactive': !plan.isActive && !plan.isTimerActive,
                    'status-timer': !plan.isActive && plan.isTimerActive
                  }"
                >
                  {{ getStatusText(plan) }}
                </span>
              </div>
            </div>
            
            <div class="plan-details">
              <div v-if="plan.requiredDeals" class="plan-progress">
                <div class="progress-label">
                  {{ helper.trans('limits.progress') }}: {{ plan.completedDeals }} / {{ plan.requiredDeals }}
                </div>
                <div class="progress-bar">
                  <div 
                    class="progress-fill" 
                    :style="{ width: getProgressPercent(plan) + '%' }"
                  ></div>
                </div>
                <div class="progress-text">{{ getProgressPercent(plan) }}%</div>
              </div>

              <div v-if="getDealAmountRangeText(plan)" class="plan-deal-amount-range mt-2">
                <div class="payment-options-label">{{ getDealAmountRangeText(plan) }}</div>
              </div>

              <div v-if="plan.allowedPaymentOptions && plan.allowedPaymentOptions.length > 0" class="plan-payment-options">
                <div class="payment-options-label">{{ helper.trans('limits.allowed_payment_options') }}</div>
                <div class="payment-options-list">
                  <span v-for="option in plan.allowedPaymentOptions" :key="option" class="payment-option-badge">
                    {{ helper.trans('payment_options.' + option) }}
                  </span>
                </div>
              </div>
              
              <div v-if="plan.isTimerActive && plan.timerExpiresAt && isTimerActive(plan.timerExpiresAt)" class="plan-timer">
                <div class="timer-label">{{ helper.trans('limits.timer') }}</div>
                <div class="timer-value">{{ formatTimer(plan.timerExpiresAt) }}</div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="no-plans">
          <p>{{ helper.trans('limits.no_plans') }}</p>
        </div>
      </div>
    </LoaderContent>
  </Popup>
</template>

<script lang="ts">
import {computed, defineComponent, ref, onMounted, onUnmounted} from 'vue'
import {reactive, toRefs} from '@vue/reactivity';
import Popup from "@/apps/public/components/Popup.vue";
import {$t, helper} from "@/service/CommonHelper";
import { useStore } from 'vuex';
import { api } from '@/service/APIClient';
import LoaderContent from "@/apps/public/components/LoaderContent.vue";
import TraderPlanDTO from "@/models/TraderPlanDTO";
import { PlanType } from "@/models/enum/PlanType";
import { PlanRewardType } from "@/models/enum/PlanRewardType";
import moment from 'moment';

export default defineComponent({
  methods: {$t},
  components: {LoaderContent, Popup},
  setup(props) {
    const loader = ref<InstanceType<typeof LoaderContent> | null>(null)
    const popup = ref<InstanceType<typeof Popup> | null>(null)
    const store = useStore()
    const state = reactive({
      plans: [] as TraderPlanDTO[],
      traderId: null as number | null,
    });
    
    const currentTime = ref(moment());

    let timerInterval: NodeJS.Timeout | null = null

    const show = (id: number) => {
      state.traderId = id
      popup.value?.show()
      loadPlans()
      startTimer()
    }

    const hide = () => {
      popup.value?.hide()
      stopTimer()
    }

    const loadPlans = async () => {
      if (!state.traderId) {
        return
      }
      
      try {
        loader.value?.load(async () => {
          const response = await api.getTraderPlans(state.traderId!)
          state.plans = (response || []).map((p: TraderPlanDTO) => ({
            ...p,
            planType: p.planType ?? PlanType.MANDATORY,
            rewardType: p.rewardType ?? PlanRewardType.UNLOCK_REQUISITES,
            rewardPriority: p.rewardPriority ?? null
          }))
        })
      } catch (error) {
        state.plans = []
      }
    }

    const getStatusText = (plan: TraderPlanDTO) => {
      if (plan.isActive) {
        return helper.trans('limits.status.active')
      } else if (plan.isTimerActive) {
        return helper.trans('limits.status.timer')
      } else {
        return helper.trans('limits.status.inactive')
      }
    }

    const getProgressPercent = (plan: TraderPlanDTO) => {
      if (!plan.requiredDeals) return 0
      return Math.min(Math.round((plan.completedDeals / plan.requiredDeals) * 100), 100)
    }

    const getDealAmountRangeText = (plan: TraderPlanDTO): string | null => {
      const min = plan.planDealAmountMin?.trim() || null
      const max = plan.planDealAmountMax?.trim() || null
      if (min && max) return helper.trans('limits.deal_amount_range', { min, max })
      if (min) return helper.trans('limits.deal_amount_from', { min })
      if (max) return helper.trans('limits.deal_amount_to', { max })
      return null
    }

    const isTimerActive = (expiresAt: string) => {
      const expires = moment(expiresAt)
      const diff = expires.diff(currentTime.value, 'seconds')
      return diff > 0
    }

    const formatTimer = (expiresAt: string) => {
      const expires = moment(expiresAt)
      const diff = expires.diff(currentTime.value, 'seconds')
      
      const hours = Math.floor(diff / 3600)
      const minutes = Math.floor((diff % 3600) / 60)
      const seconds = diff % 60
      
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    const startTimer = () => {
      if (timerInterval) {
        clearInterval(timerInterval)
      }
      timerInterval = setInterval(() => {
        currentTime.value = moment()
      }, 1000)
    }

    const stopTimer = () => {
      if (timerInterval) {
        clearInterval(timerInterval)
        timerInterval = null
      }
    }

    onMounted(() => {
      startTimer()
    })

    onUnmounted(() => {
      stopTimer()
    })

    return {
      ...toRefs(state),
      currentTime,
      loader,
      getDealAmountRangeText,
      popup,
      helper,
      show,
      hide,
      getStatusText,
      getProgressPercent,
      formatTimer,
      isTimerActive
    }
  }
})
</script>

<style scoped>
.plan-item {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
  background: #f8f9fa;
}

.plan-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.plan-currency {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.plan-type-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 2px;
}

.plan-type-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.currency-code {
  font-weight: bold;
  font-size: 16px;
  color: #333;
}

.plan-limit {
  font-size: 14px;
  color: #666;
}

.plan-status {
  display: flex;
  align-items: center;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.status-active {
  background: #d4edda;
  color: #155724;
}

.status-inactive {
  background: #f8d7da;
  color: #721c24;
}

.status-timer {
  background: #d1ecf1;
  color: #0c5460;
}

.plan-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.plan-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-label {
  font-size: 14px;
  color: #666;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #28a745;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #666;
  text-align: right;
}

.plan-timer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.timer-label {
  font-size: 12px;
  color: #666;
}

.timer-value {
  font-size: 18px;
  font-weight: bold;
  color: #dc3545;
  font-family: monospace;
}

.no-plans {
  text-align: center;
  padding: 40px;
  color: #666;
}

.plan-payment-options {
  margin-top: 12px;
}

.payment-options-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.payment-options-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.payment-option-badge {
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.plan-type-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.plan-type-badge {
  background: #e9ecef;
  color: #495057;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.plan-type-badge--priority {
  background: #cce5ff;
  color: #004085;
}
</style>